import React, { useMemo, useRef, useEffect } from "react";
import { Box, Typography, useTheme, alpha } from "@mui/material";
import CommentIcon from "@mui/icons-material/Comment";
import VideoCommentInput from "./VideoCommentInput";
import VideoCommentItem from "./VideoCommentItem";
import PublicCommentInput from "./PublicCommentInput";
import {
  VideoComment,
  isCommentActive,
  findClosestComment,
} from "@/utils/videoUtils";

interface VideoCommentsPanelProps {
  comments: VideoComment[];
  currentVideoTime: number;
  onCommentClick: (timestamp: number) => void;
  onAddComment: (comment: string, timestamp: number) => Promise<void>;
  onAddPublicComment?: (comment: string, timestamp: number, authorName: string) => Promise<void>;
  onEditComment?: (commentId: string, newText: string) => Promise<void>;
  onDeleteComment?: (commentId: string) => void;
  isLoading: boolean;
  users?: Array<{ _id: string; fullName: string }>;
  isPublic?: boolean;
  isImageMode?: boolean;
}

const VideoCommentsPanel: React.FC<VideoCommentsPanelProps> = ({
  comments,
  currentVideoTime,
  onCommentClick,
  onAddComment,
  onAddPublicComment,
  onEditComment,
  onDeleteComment,
  isLoading,
  users = [],
  isPublic = false,
  isImageMode = false,
}) => {
  const theme = useTheme();
  const listRef = useRef<HTMLDivElement>(null);
  const activeCommentRef = useRef<HTMLDivElement>(null);
  const isPublicBugPage = window?.location?.pathname.includes('public/bug');

  // Theme colors based on public/private context
  const themeColors = useMemo(() => {
    if (isPublicBugPage) {
      return {
        containerBg: "#ffffff",
        headerBg: "#f9f9f9",
        headerColor: "#333333",
        textPrimary: "#333333",
        textSecondary: "#666666",
        borderColor: "#e0e0e0",
        badgeBg: theme.palette.primary.main,
        scrollbarThumb: "rgba(0, 0, 0, 0.2)",
        emptyStateColor: "#666666",
      };
    }
    return {
      containerBg: theme.palette.background.default,
      headerBg: alpha(theme.palette.primary.main, 0.05),
      headerColor: theme.palette.primary.main,
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
      borderColor: theme.palette.divider,
      badgeBg: theme.palette.primary.main,
      scrollbarThumb: alpha(theme.palette.text.primary, 0.2),
      emptyStateColor: theme.palette.text.secondary,
    };
  }, [isPublicBugPage, theme]);


  // Filter and sort comments
  // For image mode, show all comments; for video mode, only show those with timestamps
  const videoComments = useMemo(() => {
    if (isImageMode) {
      // For images, show all comments sorted by creation date
      return [...comments].sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    }
    // For videos, filter by timestamp and sort by creation date (newest first)
    const filtered = comments.filter(
      (c) => c.videoTimestamp !== undefined && c.videoTimestamp !== null
    );
    return [...filtered].sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [comments, isImageMode]);

  // Find the closest/active comment (only for video mode)
  const activeComment = useMemo(() => {
    if (isImageMode) return null;
    return findClosestComment(videoComments, currentVideoTime);
  }, [videoComments, currentVideoTime, isImageMode]);

  // Auto-scroll to active comment when it changes
  useEffect(() => {
    if (activeComment && activeCommentRef.current && listRef.current) {
      const container = listRef.current;
      const element = activeCommentRef.current;
      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      // Only scroll if element is not fully visible
      if (
        elementRect.top < containerRect.top ||
        elementRect.bottom > containerRect.bottom
      ) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [activeComment?._id]);

  const handleCommentClick = (timestamp: number) => {
    onCommentClick(timestamp);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: themeColors.containerBg,
      }}
    >
      <Box
        sx={{
          p: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: `1px solid ${themeColors.borderColor}`,
          backgroundColor: themeColors.headerBg,
          color: themeColors.headerColor,
        }}
      >
        <CommentIcon
          sx={{ fontSize: 18 }}
        />
        <Typography variant="subtitle2" fontWeight={600}>
          Comments
        </Typography>
        <Typography
          variant="caption"
          sx={{
            backgroundColor: themeColors.badgeBg,
            color: "#fff",
            px: 1,
            py: 0.25,
            borderRadius: 10,
            fontWeight: 500,
          }}
        >
          {videoComments.length}
        </Typography>
      </Box>
      {/* Comment Input */}
      {!isPublic ? (
        <Box sx={{ p: 1.5, borderBottom: `1px solid ${themeColors.borderColor}` }}>
          <VideoCommentInput
            currentVideoTime={currentVideoTime}
            onSubmit={onAddComment}
            isSubmitting={isLoading}
            users={users}
            isImageMode={isImageMode}
          />
        </Box>
      ) : onAddPublicComment ? (
        <PublicCommentInput
          currentVideoTime={currentVideoTime}
          onSubmit={onAddPublicComment}
          isSubmitting={isLoading}
          isImageMode={isImageMode}
        />
      ) : null}

      {/* Comments List */}
      <Box
        ref={listRef}
        sx={{
          flex: 1,
          overflow: "auto",
          p: 1.5,
          color: themeColors.textPrimary,
          "&::-webkit-scrollbar": {
            width: 6,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: themeColors.scrollbarThumb,
            borderRadius: 3,
          },
        }}
      >
        {videoComments.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
              color: themeColors.emptyStateColor,
            }}
          >
            <CommentIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
            <Typography variant="body2">
              No comments yet
            </Typography>
            <Typography variant="caption" mt={0.5}>
              {isImageMode ? "Add a comment on this image" : "Add a comment at the current timestamp"}
            </Typography>
          </Box>
        ) : (
          videoComments.map((comment) => {
            const isActive = !isImageMode &&
              activeComment?._id === comment._id &&
              isCommentActive(comment.videoTimestamp || 0, currentVideoTime, 3);

            return (
              <Box
                key={comment._id}
                ref={isActive ? activeCommentRef : undefined}
              >
                <VideoCommentItem
                  comment={comment}
                  isActive={isActive}
                  onTimestampClick={handleCommentClick}
                  onEdit={isPublic ? undefined : onEditComment}
                  onDelete={isPublic ? undefined : onDeleteComment}
                  users={users}
                  isImageMode={isImageMode}
                />
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default VideoCommentsPanel;
